import React, { useState, useMemo } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { AssignmentRow } from './AssignmentRow';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Maps a directive/decision status string to its color palette.
 * Used for border accents, status dots, and progress bar fills.
 *
 * @param {string} status
 * @returns {{ border, dot, bg, progress, text }}
 */
const statusColors = (status) => {
  switch (status) {
    case 'Completed':
      return {
        border: 'border-l-green-500',
        dot: 'bg-green-500',
        bg: 'bg-green-50',
        progress: 'bg-green-500',
        text: 'text-green-700'
      };
    case 'In Progress':
      return {
        border: 'border-l-blue-500',
        dot: 'bg-blue-500',
        bg: 'bg-blue-50/30',
        progress: 'bg-blue-500',
        text: 'text-blue-700'
      };
    case 'Overdue':
      return {
        border: 'border-l-red-500',
        dot: 'bg-red-500',
        bg: 'bg-red-50',
        progress: 'bg-red-500',
        text: 'text-red-700'
      };
    case 'Stalled':
      return {
        border: 'border-l-slate-400',
        dot: 'bg-slate-400',
        bg: 'bg-slate-50',
        progress: 'bg-slate-400',
        text: 'text-slate-700'
      };
    case 'Pending':
      return {
        border: 'border-l-amber-500',
        dot: 'bg-amber-500',
        bg: 'bg-amber-50/30',
        progress: 'bg-amber-400',
        text: 'text-amber-700'
      };
    default:
      return {
        border: 'border-l-slate-300',
        dot: 'bg-slate-400',
        bg: '',
        progress: 'bg-slate-400',
        text: 'text-slate-700'
      };
  }
};

/**
 * Computes an overall progress percentage for the decision
 * based on its directives' individual progress values.
 *
 * @param {Array} directives
 * @returns {number} 0-100
 */
const computeOverallProgress = (directives) => {
  if (!directives || directives.length === 0) return 0;
  const total = directives.reduce((sum, d) => sum + (d.progress || 0), 0);
  return Math.round(total / directives.length);
};

/**
 * Computes summary counts for the directives.
 *
 * @param {Array} directives
 * @returns {{ total, completed, overdue, inProgress, pending, stalled }}
 */
const computeSummaryCounts = (directives) => {
  if (!directives || directives.length === 0) {
    return { total: 0, completed: 0, overdue: 0, inProgress: 0, pending: 0, stalled: 0 };
  }
  return {
    total: directives.length,
    completed: directives.filter(d => d.status === 'Completed').length,
    overdue: directives.filter(d => d.status === 'Overdue').length,
    inProgress: directives.filter(d => d.status === 'In Progress').length,
    pending: directives.filter(d => d.status === 'Pending').length,
    stalled: directives.filter(d => d.status === 'Stalled').length
  };
};

/**
 * DirectiveTree -- the centerpiece cascade hierarchy component.
 *
 * Renders a three-tier expandable tree:
 *   Decision (root) > Directives (middle) > Assignments (leaf)
 *
 * @param {Object} props
 * @param {Object} props.decision - The cabinet decision object (root node)
 * @param {Array}  props.directives - Directives belonging to this decision
 * @param {Array}  props.assignments - All assignments belonging to this decision's directives
 * @param {Function} props.onAssignmentAction - Callback for assignment actions (view evidence, remind, mark complete)
 * @param {Function} props.onEscalate - Callback for escalation actions
 */
export const DirectiveTree = ({
  decision,
  directives = [],
  assignments = [],
  onAssignmentAction,
  onEscalate
}) => {
  const [expandedDirectives, setExpandedDirectives] = useState({});

  const overallProgress = useMemo(
    () => computeOverallProgress(directives),
    [directives]
  );

  const summary = useMemo(
    () => computeSummaryCounts(directives),
    [directives]
  );

  /**
   * Groups assignments by their parentDirectiveId for efficient lookup.
   */
  const assignmentsByDirective = useMemo(() => {
    const map = {};
    (assignments || []).forEach(a => {
      const key = a.parentDirectiveId;
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    return map;
  }, [assignments]);

  /**
   * Toggles the expanded state for a given directive.
   */
  const toggleDirective = (directiveId) => {
    setExpandedDirectives(prev => ({
      ...prev,
      [directiveId]: !prev[directiveId]
    }));
  };

  /**
   * Wraps assignment callbacks into the shape expected by AssignmentRow.
   */
  const handleViewEvidence = (assignment) => {
    onAssignmentAction?.('viewEvidence', assignment);
  };

  const handleSendReminder = (assignment) => {
    onAssignmentAction?.('sendReminder', assignment);
  };

  const handleMarkComplete = (assignment) => {
    onAssignmentAction?.('markComplete', assignment);
  };

  const handleEscalate = (assignment) => {
    onEscalate?.(assignment);
  };

  if (!decision) return null;

  const decisionRef = decision.referenceNumber || decision.cdnNumber || '';
  const decisionTitle = decision.title || 'Untitled Decision';

  return (
    <div className="space-y-0">
      {/* ===== ROOT NODE: Decision header with overall progress ===== */}
      <div className="mb-6">
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 rounded-lg bg-grn-navy/10 text-grn-navy shrink-0">
            <SafeIcon icon={FiIcons.FiBookOpen} className="text-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {decisionRef && (
                <span className="text-xs font-mono font-bold text-grn-navy bg-grn-navy/10 px-2 py-0.5 rounded">
                  {decisionRef}
                </span>
              )}
              <h3 className="text-base font-bold text-slate-900">
                {decisionTitle}
              </h3>
            </div>

            {/* Overall progress bar */}
            <div className="mt-3 flex items-center gap-3">
              <div className="flex-1 bg-slate-200 rounded-full h-2.5">
                <div
                  className={clsx(
                    'h-2.5 rounded-full transition-all duration-500',
                    overallProgress >= 100 ? 'bg-green-500' :
                    overallProgress >= 50 ? 'bg-blue-500' :
                    overallProgress > 0 ? 'bg-amber-500' :
                    'bg-slate-300'
                  )}
                  style={{ width: `${Math.min(overallProgress, 100)}%` }}
                />
              </div>
              <span className="text-sm font-bold text-slate-700 shrink-0 min-w-[3ch] text-right">
                {overallProgress}%
              </span>
            </div>
          </div>
        </div>

        {/* Summary counts */}
        <div className="flex flex-wrap items-center gap-3 ml-12 mt-2">
          <span className="text-xs text-slate-500">
            <span className="font-semibold text-slate-700">{summary.total}</span> directive{summary.total !== 1 ? 's' : ''}
          </span>
          {summary.completed > 0 && (
            <span className="text-xs text-green-600">
              <SafeIcon icon={FiIcons.FiCheckCircle} className="inline text-xs mr-0.5" />
              {summary.completed} completed
            </span>
          )}
          {summary.inProgress > 0 && (
            <span className="text-xs text-blue-600">
              <SafeIcon icon={FiIcons.FiLoader} className="inline text-xs mr-0.5" />
              {summary.inProgress} in progress
            </span>
          )}
          {summary.overdue > 0 && (
            <span className="text-xs text-red-600 font-semibold">
              <SafeIcon icon={FiIcons.FiAlertTriangle} className="inline text-xs mr-0.5" />
              {summary.overdue} overdue
            </span>
          )}
          {summary.pending > 0 && (
            <span className="text-xs text-amber-600">
              <SafeIcon icon={FiIcons.FiClock} className="inline text-xs mr-0.5" />
              {summary.pending} pending
            </span>
          )}
          {summary.stalled > 0 && (
            <span className="text-xs text-slate-500">
              <SafeIcon icon={FiIcons.FiPauseCircle} className="inline text-xs mr-0.5" />
              {summary.stalled} stalled
            </span>
          )}
        </div>
      </div>

      {/* ===== DIRECTIVE NODES (middle tier) ===== */}
      <div className="relative">
        {/* Main vertical connector line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200" />

        <div className="space-y-3">
          {directives.map((directive, index) => {
            const colors = statusColors(directive.status);
            const isExpanded = !!expandedDirectives[directive.id];
            const directiveAssignments = assignmentsByDirective[directive.id] || [];
            const progress = directive.progress || 0;
            const formattedDeadline = directive.deadline
              ? format(new Date(directive.deadline), 'dd MMM yyyy')
              : 'No deadline';

            return (
              <div key={directive.id} className="relative pl-12">
                {/* Horizontal connector line from vertical line to directive card */}
                <div className="absolute left-6 top-5 w-6 h-0.5 bg-slate-200" />

                {/* Status dot */}
                <div
                  className={clsx(
                    'absolute left-4 top-3 h-5 w-5 rounded-full border-2 border-white shadow-sm z-10',
                    colors.dot
                  )}
                />

                {/* Directive Card */}
                <Card
                  className={clsx(
                    'border-l-2 overflow-hidden transition-all',
                    colors.border,
                    directive.status === 'Overdue' && 'bg-red-50 border-red-200'
                  )}
                >
                  {/* Directive header -- clickable to expand/collapse */}
                  <button
                    type="button"
                    onClick={() => toggleDirective(directive.id)}
                    className="w-full text-left p-0 bg-transparent border-none cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {directive.referenceCode && (
                            <span className="text-[10px] font-mono font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                              {directive.referenceCode}
                            </span>
                          )}
                          <h4 className="text-sm font-semibold text-slate-900">
                            {directive.title || 'Untitled Directive'}
                          </h4>
                        </div>

                        {/* Ministry + deadline row */}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                          {directive.assignedMinistry && (
                            <span className="text-xs text-slate-600 flex items-center gap-1">
                              <SafeIcon icon={FiIcons.FiHome} className="text-xs text-slate-400" />
                              {directive.assignedMinistry}
                            </span>
                          )}
                          <span className={clsx(
                            'text-xs flex items-center gap-1',
                            directive.status === 'Overdue' ? 'text-red-600 font-semibold' : 'text-slate-500'
                          )}>
                            <SafeIcon icon={FiIcons.FiCalendar} className="text-xs" />
                            {formattedDeadline}
                          </span>
                          {directiveAssignments.length > 0 && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <SafeIcon icon={FiIcons.FiUsers} className="text-xs" />
                              {directiveAssignments.length} assignment{directiveAssignments.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right side: progress + badge + chevron */}
                      <div className="flex items-center gap-3 shrink-0">
                        {/* Mini progress bar */}
                        <div className="hidden sm:flex items-center gap-2">
                          <div className="w-16 bg-slate-200 rounded-full h-1.5">
                            <div
                              className={clsx('h-1.5 rounded-full transition-all', colors.progress)}
                              style={{ width: `${Math.min(progress, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-slate-600 min-w-[3ch] text-right">
                            {progress}%
                          </span>
                        </div>

                        <Badge status={directive.status} size="sm">
                          {directive.status}
                        </Badge>

                        <SafeIcon
                          icon={FiIcons.FiChevronDown}
                          className={clsx(
                            'text-slate-400 transition-transform duration-200',
                            isExpanded && 'rotate-180'
                          )}
                        />
                      </div>
                    </div>
                  </button>

                  {/* ===== ASSIGNMENT NODES (leaf tier) -- expand/collapse ===== */}
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        key={`assignments-${directive.id}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-border mt-3 pt-3">
                          {directiveAssignments.length > 0 ? (
                            <div className="relative pl-8 space-y-3">
                              {/* Inner vertical connector for assignments */}
                              <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-slate-200" />

                              {directiveAssignments.map((assignment, aIdx) => (
                                <div key={assignment.id} className="relative">
                                  {/* Horizontal branch to assignment */}
                                  <div className="absolute -left-5 top-5 w-5 h-0.5 bg-slate-200" />

                                  {/* Small status dot for assignment */}
                                  <div
                                    className={clsx(
                                      'absolute -left-6 top-3.5 h-3 w-3 rounded-full border-2 border-white shadow-sm z-10',
                                      statusColors(assignment.status).dot
                                    )}
                                  />

                                  <AssignmentRow
                                    assignment={assignment}
                                    variant="tree"
                                    onViewEvidence={handleViewEvidence}
                                    onSendReminder={handleSendReminder}
                                    onMarkComplete={handleMarkComplete}
                                    onEscalate={handleEscalate}
                                  />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="pl-8 py-4 text-center">
                              <SafeIcon icon={FiIcons.FiInbox} className="mx-auto text-2xl text-slate-300 mb-2" />
                              <p className="text-xs text-slate-400">
                                No assignments created for this directive yet.
                              </p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              </div>
            );
          })}
        </div>
      </div>

      {/* Empty state when no directives exist */}
      {directives.length === 0 && (
        <div className="text-center py-8">
          <SafeIcon icon={FiIcons.FiGitBranch} className="mx-auto text-3xl text-slate-300 mb-3" />
          <p className="text-sm text-slate-500 mb-1">No directives found for this decision.</p>
          <p className="text-xs text-slate-400">
            Directives will appear here once they are created and linked to this decision.
          </p>
        </div>
      )}
    </div>
  );
};
